import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, Crown, Shield, TrendingUp, AlertTriangle, ArrowUp } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { useMainButton } from '@/hooks/useMainButton';
import {
  useCoachDeckMutation,
  useMeQuery,
  useProfileMeQuery,
} from '@/services/qbotApi';
import { Paywall } from '@/components/cr/Paywall';
import { DeckGrid } from '@/components/cr/DeckGrid';
import type { QbotApiError } from '@/types/qbot';

function isQbotApiError(err: unknown): err is QbotApiError {
  return typeof err === 'object' && err !== null && 'data' in err;
}

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  body: string;
}

function Section({ icon, title, body }: SectionProps) {
  return (
    <section className="rounded-xl bg-bg-secondary p-3">
      <h2 className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-brand">
        <span className="text-brand">{icon}</span>
        {title}
      </h2>
      <p className="whitespace-pre-line text-sm leading-relaxed text-fg">{body}</p>
    </section>
  );
}

/**
 * Ramon AI coach — one-shot analysis of the linked player's current deck.
 *
 * Tier gate: free users get the Paywall component instead. Pro users see
 * a "Get analysis" CTA (wired to Telegram MainButton); on tap we POST
 * /coach/deck/:tag and render the structured response.
 *
 * Rate-limit (10/h) is enforced server-side; on 429 we surface the
 * retryAfterSeconds.
 */
export function CRCoach() {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();
  const { data: identity, isLoading: identityLoading } = useMeQuery();
  const { data: profile, error: profileError } = useProfileMeQuery();
  const [requestCoach, { data, isLoading, error, reset }] = useCoachDeckMutation();

  const tag = profile?.savedTag ?? profile?.player?.tag?.replace('#', '') ?? '';
  const isPro = identity?.tier === 'pro' || identity?.tier === 'admin';

  // Forward to link page if not yet linked (mirrors other screens)
  if (profileError) {
    const code = isQbotApiError(profileError) ? profileError.data?.error?.code : undefined;
    if (code === 'NOT_LINKED' || code === 'NOT_FOUND') return <Navigate to="/cr/link" replace />;
  }

  // Auto-fire once when the user lands on the page and is allowed to call.
  // Subsequent re-fires happen via the MainButton.
  useEffect(() => {
    if (isPro && tag && !data && !isLoading && !error) {
      requestCoach({ tag }).catch(() => undefined);
    }
  }, [isPro, tag, data, isLoading, error, requestCoach]);

  useMainButton({
    text: data ? t('coach.cta.refresh') : t('coach.cta.start'),
    enabled: isPro && !!tag && !isLoading,
    loading: isLoading,
    onClick: () => {
      if (!tag) return;
      reset();
      haptics.impact('medium');
      requestCoach({ tag }).catch(() => undefined);
    },
  });

  if (identityLoading) {
    return <div className="px-4 py-6 text-sm text-fg-muted">{t('coach.loading')}</div>;
  }

  if (!isPro) {
    return (
      <div className="flex h-full items-center justify-center px-4">
        <Paywall feature={t('coach.title')} />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto px-4 py-4">
      <header>
        <h1 className="text-xl font-bold text-fg">{t('coach.title')}</h1>
        <p className="text-[11px] text-fg-muted">{t('coach.byline')}</p>
      </header>

      {profile?.player.currentDeck?.length === 8 && (
        <section className="rounded-xl bg-bg-secondary p-3">
          <DeckGrid cards={profile.player.currentDeck} />
        </section>
      )}

      {isLoading && (
        <div className="flex items-center justify-center gap-2 rounded-xl bg-bg-secondary p-6 text-sm text-fg-muted">
          <Sparkles className="h-4 w-4 animate-pulse text-brand" />
          {t('coach.thinking')}
        </div>
      )}

      {!!error && (
        <p className="rounded-md bg-rose-500/10 p-3 text-sm text-rose-300">
          {coachErrorMessage(error, t)}
        </p>
      )}

      {data?.analysis && (
        <>
          <Section
            icon={<Crown className="h-4 w-4" />}
            title={t('coach.section.winCondition')}
            body={data.analysis.winCondition}
          />
          <Section
            icon={<TrendingUp className="h-4 w-4" />}
            title={t('coach.section.strengths')}
            body={data.analysis.strengths}
          />
          <Section
            icon={<Shield className="h-4 w-4" />}
            title={t('coach.section.weaknesses')}
            body={data.analysis.weaknesses}
          />
          <Section
            icon={<AlertTriangle className="h-4 w-4" />}
            title={t('coach.section.watchOutFor')}
            body={data.analysis.watchOutFor}
          />
          <Section
            icon={<ArrowUp className="h-4 w-4" />}
            title={t('coach.section.upgradePriority')}
            body={data.analysis.upgradePriority}
          />
          {data.usage && (
            <p className="text-[10px] text-fg-muted">
              {t('coach.usage', {
                model: data.usage.model,
                cost: data.usage.costUsd.toFixed(4),
                ms: data.usage.latencyMs,
              })}
            </p>
          )}
        </>
      )}
    </div>
  );
}

function coachErrorMessage(
  err: unknown,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string {
  if (!isQbotApiError(err)) return t('coach.error.generic');
  const code = err.data?.error?.code;
  if (code === 'QUOTA_EXCEEDED') {
    const details = err.data?.error?.details as { retryAfterSeconds?: number } | undefined;
    const seconds = details?.retryAfterSeconds ?? 0;
    return t('coach.error.quota', {
      minutes: Math.max(1, Math.ceil(seconds / 60)),
    });
  }
  if (code === 'TIER_REQUIRED') return t('coach.error.tier');
  if (code === 'INVALID_DECK') return t('coach.error.noDeck');
  return t('coach.error.generic');
}
